import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAccessToken } from "@/lib/storeManager";
import { useAuth } from "../contexts/AuthContext";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Detects which portal the user is on based on the current URL path
 * and returns the matching auth redirect path.
 */
function getPortalAuthPath(pathname: string): string {
  if (
    pathname.includes("/super-admin") ||
    pathname.includes("/dashboard/super-admin")
  ) {
    return "/jainboarding-admin/auth?portal=super_admin";
  }
  if (
    pathname.includes("/hostel") ||
    pathname.includes("/dashboard/hostel-admin")
  ) {
    return "/jainboarding-admin/auth?portal=hostel";
  }
  if (
    pathname.includes("/institution") ||
    pathname.includes("/dashboard/institution-admin")
  ) {
    return "/jainboarding-admin/auth?portal=institution";
  }
  if (
    pathname.includes("/student") ||
    pathname.includes("/dashboard/student")
  ) {
    return "/jainboarding-admin/auth?portal=student";
  }
  if (pathname.includes("/donor") || pathname.includes("/dashboard/donor")) {
    return "/jainboarding-admin/auth?portal=donor";
  }
  if (
    pathname.includes("/trustee") ||
    pathname.includes("/dashboard/trustee")
  ) {
    return "/jainboarding-admin/auth?portal=trustee";
  }
  // Fallback to portal selection
  return "/";
}

/**
 * Wraps routes that require authentication.
 * If no access token is found, redirects to the specific portal's login page.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { loading } = useAuth();
  const location = useLocation();
  const token = getAccessToken();

  // While auth state is loading, show nothing (avoids flash redirect)
  if (loading) {
    return null;
  }

  if (!token) {
    const redirectPath = getPortalAuthPath(location.pathname);
    return <Navigate to={redirectPath} replace />;
  }

  return children as React.ReactElement;
}
